// frontend/src/utils/imageCompression.ts
// Automatic pre-compression of photos before upload, applied only when it is
// both possible and worthwhile.
//
// Two independent levers, grounded in how the Claude vision API actually works
// (platform.claude.com/docs/en/build-with-claude/vision):
//
//   • DIMENSIONS drive the AI cost (one visual token per 28×28 px block) and
//     the model on the high-resolution tier sees at most 2576 px on the long
//     edge — anything larger is downscaled server-side anyway. Resizing to
//     2576 px therefore never removes information the model could have used.
//     It also keeps clear of the HARD limits (8000 px, 10 MB base64) where the
//     API rejects instead of downscaling.
//   • BYTES drive upload time, database size (the original is stored in-row as
//     base64) and request payloads. A single JPEG pass at high quality shrinks
//     a phone photo several-fold without hurting legibility; the docs warn
//     against repeated or aggressive compression, so an image is never
//     recompressed twice and quality stays at 0.85.
//
// Deliberately untouched: PDFs (parsed natively, better than rasterizing),
// GIFs (only the first frame would survive a canvas pass), CSV/TXT, and any
// image that is already small enough — «possible et désirable», pas
// systématique. On any decoding failure the original file is used unchanged:
// compression must never block an upload.

/** Long edge the high-resolution vision tier natively supports (Claude 4.7+). */
export const MAX_LONG_EDGE_PX = 2576;

/** Below this byte size, recompression is not worth a lossy pass. */
export const RECODE_THRESHOLD_BYTES = 2 * 1024 * 1024;

/** Single-pass JPEG quality — high enough to keep invoice small print crisp. */
export const JPEG_QUALITY = 0.85;

/** Raw-byte ceiling above which the API would reject the image outright
 *  (10 MB measured on the base64 encoding ⇒ ≈ 7.5 MB of raw bytes). */
export const API_HARD_LIMIT_BYTES = 7 * 1024 * 1024;

/** Long-edge ceiling above which the API rejects instead of downscaling. */
export const API_HARD_LIMIT_PX = 8000;

const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

export interface CompressionPlan {
  action: 'keep' | 'process';
  targetWidth: number;
  targetHeight: number;
  reason: 'not-an-image' | 'already-small' | 'resize' | 'recode';
}

/**
 * Pure decision: what should happen to an image of these dimensions and size?
 * Split from the DOM work so the policy is readable — and testable — on its own.
 */
export function planImageCompression(input: {
  mimeType: string;
  bytes: number;
  width: number;
  height: number;
}): CompressionPlan {
  const { mimeType, bytes, width, height } = input;
  const keep: CompressionPlan = {
    action: 'keep', targetWidth: width, targetHeight: height, reason: 'already-small',
  };

  if (!COMPRESSIBLE_TYPES.has(mimeType.toLowerCase())) {
    return { ...keep, reason: 'not-an-image' };
  }

  const longEdge = Math.max(width, height);
  const scale = longEdge > MAX_LONG_EDGE_PX ? MAX_LONG_EDGE_PX / longEdge : 1;

  if (scale < 1) {
    return {
      action: 'process',
      targetWidth: Math.max(1, Math.round(width * scale)),
      targetHeight: Math.max(1, Math.round(height * scale)),
      reason: 'resize',
    };
  }

  if (bytes > RECODE_THRESHOLD_BYTES) {
    return { action: 'process', targetWidth: width, targetHeight: height, reason: 'recode' };
  }

  return keep;
}

/**
 * True when the ORIGINAL would be refused by the vision API outright — in that
 * case the processed version must be used even if it happened to be larger.
 */
export function violatesApiHardLimits(bytes: number, width: number, height: number): boolean {
  return bytes > API_HARD_LIMIT_BYTES || Math.max(width, height) > API_HARD_LIMIT_PX;
}

export interface CompressionResult {
  file: File;
  /** True when the returned file is the untouched original. */
  original: boolean;
  beforeBytes: number;
  afterBytes: number;
}

/**
 * Compress one file for upload, when possible and worthwhile.
 *
 * `createImageBitmap(..., { imageOrientation: 'from-image' })` bakes the EXIF
 * rotation into the pixels — canvas re-encoding strips EXIF, so without this a
 * phone photo taken in portrait would arrive lying on its side. PNG
 * transparency is flattened onto white (JPEG has no alpha; the default would
 * be black, which can swallow dark text).
 */
export async function compressImageForUpload(file: File): Promise<CompressionResult> {
  const untouched: CompressionResult = {
    file, original: true, beforeBytes: file.size, afterBytes: file.size,
  };

  if (!COMPRESSIBLE_TYPES.has((file.type || '').toLowerCase())) return untouched;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return untouched; // undecodable here ⇒ let the server / API be the judge
  }

  try {
    const plan = planImageCompression({
      mimeType: file.type, bytes: file.size, width: bitmap.width, height: bitmap.height,
    });
    if (plan.action === 'keep') return untouched;

    const canvas = document.createElement('canvas');
    canvas.width = plan.targetWidth;
    canvas.height = plan.targetHeight;
    const context = canvas.getContext('2d');
    if (!context) return untouched;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) return untouched;

    // Smaller bytes win. The one exception: an original the API would reject
    // outright must be replaced regardless — a bigger accepted file beats a
    // smaller rejected one.
    const mustReplace = violatesApiHardLimits(file.size, bitmap.width, bitmap.height);
    if (!mustReplace && blob.size >= file.size) return untouched;

    const renamed = file.name.replace(/\.(png|webp|jpeg|jpg)$/i, '') + '.jpg';
    return {
      file: new File([blob], renamed, { type: 'image/jpeg', lastModified: file.lastModified }),
      original: false,
      beforeBytes: file.size,
      afterBytes: blob.size,
    };
  } finally {
    bitmap.close();
  }
}
